import { BlockError } from '@/blocks.04-project/block-error/block-error';
import toNumber from 'lodash-es/toNumber';
import debounce from 'lodash-es/debounce';

/***
 * Класс боковой панели
 * @example
 * Аттрибуты:
 * data-slider-min-width {number, default = 991} - минимальный размер экрана для работы боковой панели
 * data-slider-top-spacing {number, default = 20} - отступ сверху
 * data-slider-bottom-spacing {number, default = 0} - отступ снизу
 * data-inner-top {number, default = 0} - отступ триггера сверху
 * data-slider-container {selector, default = body} - селектор родительского элемента со скроллом
 *
 * Методы:
 * update - обновляет положение боковой панели
 *
 * События:
 * iv.sidebar.update - испускается при обновлении положение панели
 *
 * Библиотека
 * @see {@link https://github.com/somewebmedia/hc-sticky}
 */
class Sidebar { //TODO при маленькой высоте экрана боковая панель перестаёт скроллиться.
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с классом sidebar
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'Sidebar', destroy: this.destroy });

    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.Sidebar = this;
    this.block.AsyncSidebar = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.init();
  }

  init = async () => {
    this.headerElem = document.querySelector('.header');
    if (!this.headerElem) return this.BlockError.error('Отсутствует .header');

    this.Header = this.headerElem.Header;
    if (!this.Header) return this.BlockError.error('Отсутствует Header у .header');

    this.setOptions();

    const { default: hcSticky } = await import(/* webpackChunkName: "HcSticky" */ 'hc-sticky').catch((err) => {
      throw new Error(err);
    });

    this.sidebar = new hcSticky(this.inner, {
      stickTo: this.containerSelector,
      top: this.topSpacing,
      bottom: this.bottomSpacing,
      innerTop: this.innerTop,
      responsive: {
        [this.minWidth]: {
          disable: true,
        }
      },
    });

    this.resizeUpdate();
    window.addEventListener('resize', this.resizeUpdate);

    this.promiseResolve(this);
    this.block.AsyncProductCard = this;

    this.block.classList.add('sidebar_inited');
  };

  resizeUpdate = debounce(() => {
    this.topSpacing = this.dataTopSpacing + toNumber(this.Header.getOffsetTop());
		const bottomPadding = window.innerWidth > this.minWidth && this.inner.clientHeight >= window.innerHeight - this.topSpacing ? this.topSpacing : 0;

    this.inner.style.paddingBottom = bottomPadding + 'px';

    this.sidebar.update({
      top: this.topSpacing,
      bottom: this.bottomSpacing - bottomPadding,
      bottomEnd: -bottomPadding
    });

    this.update();
  }, 300);

  update = () => {
    // Метод this.sidebar.updateSticky() работает плохо
    if (this.sidebar) {
      this.sidebar.refresh();
      this.block.dispatchEvent(new Event('iv.sidebar.update', { bubbles: true }));
    }
  };

  setOptions = () => {
    this.sidebar = null;
    this.minWidth = this.block.getAttribute('data-slider-min-width') || 991;
    this.dataTopSpacing = toNumber(this.block.getAttribute('data-slider-top-spacing')) || 20;
    this.topSpacing = this.dataTopSpacing + toNumber(this.Header.getOffsetTop());
    this.bottomSpacing = toNumber(this.block.getAttribute('data-slider-bottom-spacing')) || 0;
    this.innerTop = toNumber(this.block.getAttribute('data-slider-inner-top')) || 0;
    this.containerSelector = this.block.getAttribute('data-slider-container') || null;
    this.inner = this.block.querySelector('.sidebar__inner');
  };

  destroy = () => {
    if (this.sidebar) this.sidebar.destroy();
    if (this.LazyInit) this.LazyInit.destroy();
    window.removeEventListener('resize', this.resizeUpdate);
    if (this.block) {
      this.block.classList.remove('sidebar_inited');
      delete this.block.Sidebar;
      delete this.block.AsyncSidebar;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('.sidebar:not(.sidebar_inited)')).forEach((elem) => new Sidebar(elem));
  }
}

document.addEventListener('DOMContentLoaded', Sidebar.init);

// Экспорт
window.Block.Sidebar = Sidebar;
